//
// Copyright (c) 2011-2025 by Curalate, Inc.
//

import moment from 'moment-timezone';

import {
  AudienceTargetingSections,
  BOX_TYPE_BRANDED,
  BOX_TYPE_CLIENT_PROVIDED,
  BOX_TYPE_PLAIN,
  DisplayReviews,
  FulfillmentDetails,
  FulfillmentType,
  MemberTasks,
  PACKAGE_INSERT_CLIENT_PROVIDED,
  PACKAGE_INSERT_NONE,
  QUOTE_LOADING_STATUSES,
  SamplingCampaignRequestLocale,
  SamplingCampaignRequestQuoteStatus,
  WhoShipsProduct,
} from '@apis/campaign-requests/sampling/campaign-requests/samplingCampaignRequestsApiModel';
import {
  PurchaseOption,
  SamplingContentType,
  SamplingDeliveryMethod,
  SamplingOpportunityCartonType,
  SamplingOpportunityInsertType,
} from '@apis/campaign-requests/sampling/opportunities/samplingOpportunitiesApiModel';
import { CAMPAIGN_REQUEST_LOCALES } from '@campaigns/constants/CampaignRequestsLocaleConstants';
import { DATE_FORMAT_MMM_DD_YYYY } from '@campaigns/constants/DateConstants';
import {
  BV_SAMPLING_PROGRAM,
  EXPANDED_SECTIONS_MAPPING,
} from '@campaigns/constants/sampling/SamplingCampaignRequestConstants';
import { DEFAULT_EMPTY_VALUE } from '@constants/DefaultEmptyValues';
import { isDefined } from '@utils/filterPredicates';

export const isBVSamplingProgram = (program: string) =>
  program === BV_SAMPLING_PROGRAM;

export const getLocaleDetails = (value?: SamplingCampaignRequestLocale) => {
  if (!value) {
    return undefined;
  }
  return CAMPAIGN_REQUEST_LOCALES.find((locale) => locale.value === value);
};

export const getWhoShipsProductText = (
  whoShipsProduct: WhoShipsProduct,
): string => {
  if (whoShipsProduct === 'bazaarvoice') {
    return 'Shipped by Bazaarvoice';
  }

  return 'Shipped by my company';
};

export const getSamplingProgramText = (
  samplingProgram: string | undefined,
): string => {
  if (samplingProgram) {
    return isBVSamplingProgram(samplingProgram)
      ? 'Bazaarvoice Sampling Community'
      : 'Client Community';
  } else {
    return DEFAULT_EMPTY_VALUE;
  }
};

export const getManagedByText = (
  fulfillmentType: FulfillmentType | undefined,
) => {
  if (fulfillmentType === 'shipped') {
    return 'Shipped products provided';
  } else if (fulfillmentType === 'deals') {
    return 'Coupon codes provided';
  }

  return undefined;
};

export const getFulfillmentOptionsFromPurchaseOption = (
  purchaseOption: PurchaseOption,
): FulfillmentDetails => {
  let fulfillmentDetails: FulfillmentDetails = {};

  switch (purchaseOption.deliveryMethod) {
    case SamplingDeliveryMethod.Shipped:
      fulfillmentDetails = {
        fulfillmentType: 'shipped',
        whoShipsProduct: 'myCompany',
      };
      break;
    case SamplingDeliveryMethod.ShippedByBazaarvoice:
      fulfillmentDetails = {
        fulfillmentType: 'shipped',
        whoShipsProduct: 'bazaarvoice',
      };
      break;
    case SamplingDeliveryMethod.DigitalDelivery:
      fulfillmentDetails = {
        fulfillmentType: 'deals',
      };
      break;
    default:
      return {};
  }

  const { fulfillmentOptions } = purchaseOption;

  if (fulfillmentOptions && fulfillmentOptions.length > 0) {
    const firstInsert = fulfillmentOptions[0]?.insert;
    const allSame = fulfillmentOptions.every(
      (option) => option.insert === firstInsert,
    );

    if (allSame && firstInsert) {
      if (firstInsert === SamplingOpportunityInsertType.NO) {
        fulfillmentDetails.packageInsert = PACKAGE_INSERT_NONE;
      } else if (
        firstInsert === SamplingOpportunityInsertType.CLIENT_SUPPLIED
      ) {
        fulfillmentDetails.packageInsert = PACKAGE_INSERT_CLIENT_PROVIDED;
      }
    }

    const firstCartonType = fulfillmentOptions[0]?.cartonType;
    const allSameCartonType = fulfillmentOptions.every(
      (option) => option.cartonType === firstCartonType,
    );

    if (allSameCartonType && firstCartonType) {
      if (firstCartonType === SamplingOpportunityCartonType.PLAIN) {
        fulfillmentDetails.boxType = BOX_TYPE_PLAIN;
      } else if (
        firstCartonType === SamplingOpportunityCartonType.CLIENT_SUPPLIED
      ) {
        fulfillmentDetails.boxType = BOX_TYPE_CLIENT_PROVIDED;
      } else if (
        firstCartonType === SamplingOpportunityCartonType.TWO_SIDED_CUSTOM
      ) {
        fulfillmentDetails.boxType = BOX_TYPE_BRANDED;
      }
    }
  }

  return fulfillmentDetails;
};

export const getMemberTasksFromPurchaseOption = (
  purchaseOption: PurchaseOption,
): { displayReviews: DisplayReviews; memberTasks: MemberTasks } => {
  const { contentType, hasPostSurvey } = purchaseOption;

  const memberTasks: MemberTasks = {
    postOnSocial: false,
    postSurvey: false,
    writeReview: false,
  };
  const displayReviews: DisplayReviews = {};

  if (contentType === SamplingContentType.REVIEWS_ONLY) {
    memberTasks.writeReview = true;
  } else if (contentType === SamplingContentType.SOCIAL_ONLY) {
    memberTasks.postOnSocial = true;
  } else if (contentType === SamplingContentType.REVIEWS_AND_SOCIAL) {
    memberTasks.writeReview = true;
    memberTasks.postOnSocial = true;
  }

  if (hasPostSurvey) {
    memberTasks.postSurvey = true;
  }

  return { displayReviews, memberTasks };
};

export const isKeyOfAudienceTargetingSections = (
  key: string,
): key is keyof AudienceTargetingSections => {
  return key in EXPANDED_SECTIONS_MAPPING;
};

export const getNotificationsTitle = (
  whoShipsProduct: WhoShipsProduct | undefined,
) => {
  return whoShipsProduct === 'bazaarvoice'
    ? 'Warehouse notifications'
    : 'Fulfillment file notifications';
};

export const filterValidAndSelectedOpportunities = (
  opportunities: PurchaseOption[] | undefined,
  selectedOptions: PurchaseOption[],
): PurchaseOption[] => {
  const currentDate = moment();

  return (
    opportunities?.filter((opportunity) => {
      const expiryDate = moment(opportunity.expiryDate);
      const isExpired =
        expiryDate.isValid() && expiryDate.isBefore(currentDate);
      const isSelected = selectedOptions.some(
        (selectedOption) =>
          selectedOption.opportunityRef === opportunity.opportunityRef,
      );
      const hasPositiveBalance =
        isDefined(opportunity.balance) && opportunity.balance > 0;

      return (hasPositiveBalance && !isExpired) || isSelected;
    }) ?? []
  );
};

export const haveProductsInCampaignLaunched = (
  targetPublishDate: Date | undefined,
) => {
  return (
    !targetPublishDate ||
    (targetPublishDate && moment(targetPublishDate).isBefore(moment()))
  );
};

const CAMPAIGN_ID_DISPLAY_LENGTH = 6;
const DEFAULT_CAMPAIGN_NAME = 'New Campaign';

export const formatCampaignDisplayName = (
  ugcCampaignRequestId: string | undefined,
  clientCampaignName: string | undefined,
): string => {
  if (!isDefined(ugcCampaignRequestId)) {
    return clientCampaignName ?? DEFAULT_CAMPAIGN_NAME;
  }

  const truncatedId = ugcCampaignRequestId
    .substring(0, CAMPAIGN_ID_DISPLAY_LENGTH)
    .toUpperCase();
  const campaignName = clientCampaignName ?? DEFAULT_CAMPAIGN_NAME;

  return `${truncatedId} | ${campaignName}`;
};

export const formatCampaignDates = (createdAt?: Date, expiredAt?: Date) => {
  if (isDefined(createdAt) && isDefined(expiredAt)) {
    return `${moment(createdAt).format(DATE_FORMAT_MMM_DD_YYYY)} - ${moment(expiredAt).format(DATE_FORMAT_MMM_DD_YYYY)}`;
  }

  return undefined;
};

export enum QuoteOutcome {
  VALID_QUOTE = 'VALID_QUOTE',
  EXPIRED_QUOTE = 'EXPIRED_QUOTE',
  NOT_QUOTABLE = 'NOT_QUOTABLE',
  ERROR = 'ERROR',
  LOADING = 'LOADING',
}

export const determineQuoteOutcome = ({
  isQuoteError,
  isQuoteValid,
  status,
}: {
  isQuoteError: boolean;
  isQuoteValid: boolean;
  status?: SamplingCampaignRequestQuoteStatus;
}): QuoteOutcome | undefined => {
  if (
    isQuoteError ||
    (isQuoteValid &&
      (status === SamplingCampaignRequestQuoteStatus.ERROR_INITIALISING ||
        status === SamplingCampaignRequestQuoteStatus.ERROR_QUOTING))
  ) {
    return QuoteOutcome.ERROR;
  }

  if (
    isDefined(status) &&
    !isQuoteValid &&
    QUOTE_LOADING_STATUSES.includes(status)
  ) {
    return QuoteOutcome.LOADING;
  }

  if (isQuoteValid) {
    switch (status) {
      case SamplingCampaignRequestQuoteStatus.QUOTED:
        return QuoteOutcome.VALID_QUOTE;
      case SamplingCampaignRequestQuoteStatus.EXPIRED:
        return QuoteOutcome.EXPIRED_QUOTE;
      case SamplingCampaignRequestQuoteStatus.NOT_QUOTABLE:
        return QuoteOutcome.NOT_QUOTABLE;
    }
  }

  return undefined;
};
